import type { Me, Settings } from '../api/types'

/**
 * Die Farben der Reiseübersicht (#54) — und die Kette, an deren Ende sie gelten.
 *
 * Drei Werte: die Zeile einer **eigenen** Reise, die Zeile einer **fremden**,
 * und die **leere** Fläche darunter. Zwei Zeilenfarben und nicht eine, weil die
 * eigene Reise sich abheben soll (#53) — den Streifen an ihrer linken Kante
 * trägt das Stylesheet zusätzlich, denn eine Markierung, die allein an einer
 * wählbaren Farbe hängt, kann man versehentlich abschalten.
 */
export const LISTENFARBEN = [
  {
    key: 'own',
    einstellung: 'list_own_color',
    titel: 'Eigene Reisen',
    hinweis: 'Berichte, die du selbst schreibst.',
    variable: '--liste-eigen',
    vorgabe: '#e8f0fe',
  },
  {
    key: 'other',
    einstellung: 'list_other_color',
    titel: 'Reisen anderer',
    hinweis: 'Berichte, bei denen du mitliest.',
    variable: '--liste-fremd',
    vorgabe: '#ffffff',
  },
  {
    key: 'bg',
    einstellung: 'list_bg_color',
    titel: 'Freie Fläche',
    hinweis: 'Was unter der Liste steht, wo keine Reise mehr kommt.',
    variable: '--liste-grund',
    vorgabe: '#f2f2f7',
  },
] as const

/** Der Name einer Listenfarbe, wie ihn die Oberfläche führt. */
export type Listenfarbe = (typeof LISTENFARBEN)[number]['key']

/** Ein vollständiger Satz — leer heißt „nichts gesetzt". */
export type Listensatz = Record<Listenfarbe, string>

export const LEER: Listensatz = { own: '', other: '', bg: '' }

/**
 * **Die Kette: das eigene Konto schlägt die Voreinstellung** (#54).
 *
 * Zwei Stufen, und `''` heißt jeweils „eine Stufe höher". Ganz oben heißt es
 * „wie das Erscheinungsbild" — dann wird die Variable **weggenommen** und nicht
 * gesetzt; nur so behält die dunkle Darstellung ihren eigenen Ton.
 *
 * **Vererbt und nicht kopiert**, anders als die Blasenfarbe einer Reise (#49):
 * dort soll ein laufender Bericht sein Aussehen behalten, wenn jemand anderswo
 * etwas dreht. Hier ist das Gegenteil gewünscht und steht so im Issue — „mit
 * einem Knopf auf die Voreinstellung zurück" heißt, dass sie weiter gilt,
 * solange nichts Eigenes dasteht.
 *
 * Als reine Funktion, damit ein Lauf sie messen kann.
 */
export function listenKette(
  konto: Partial<Listensatz> | null | undefined,
  vorein: Partial<Listensatz> | null | undefined,
): Listensatz {
  const nimm = (welche: Listenfarbe) => konto?.[welche] || vorein?.[welche] || ''
  return { own: nimm('own'), other: nimm('other'), bg: nimm('bg') }
}

/** Was der Server liefert, auf drei Zeichenketten gebracht. */
export function ausKonto(me: Me | undefined): Listensatz {
  return {
    own: me?.list_colors?.own ?? '',
    other: me?.list_colors?.other ?? '',
    bg: me?.list_colors?.bg ?? '',
  }
}

/** Dasselbe aus den allgemeinen Einstellungen. */
export function ausEinstellungen(werte: Settings | undefined): Listensatz {
  return {
    own: werte?.list_own_color ?? '',
    other: werte?.list_other_color ?? '',
    bg: werte?.list_bg_color ?? '',
  }
}

/**
 * Die Variablen für einen Satz Farben.
 *
 * Was leer bleibt, kommt **nicht** vor: der Aufrufer nimmt eine fehlende
 * Variable wieder weg, statt sie auf einen Wert zu setzen — dieselbe Regel wie
 * bei den Sprechblasen.
 */
export function listenVariablen(satz: Listensatz): Record<string, string | null> {
  const raus: Record<string, string | null> = {}
  for (const eintrag of LISTENFARBEN) {
    const farbe = satz[eintrag.key]
    raus[eintrag.variable] = farbe === '' ? null : farbe
  }
  return raus
}
